// v-overlay-scrollbar: draws a custom thumb over a single scroller while
// hiding that scroller's native scrollbar. Replaces the imperative overlay in
// ConversationView, which (P1) hid every descendant's scrollbar via a global
// `::v-deep *` rule — including 3rd-party MF mounts — (P2) shared one thumb
// across the whole subtree, and (P3) ran getBoundingClientRect + style writes
// per uncoalesced scroll event. Here each bound element owns its thumb, hides
// only its own scrollbar, and positions by cheap ratio reads on rAF.
//
// The thumb is persistent: visible whenever the content overflows, from mount
// on — users without a scroll wheel need a visible bar to drag, so it must not
// depend on a scroll event to appear nor fade out while idle.
//
// Usage: <div class="scrollable" v-overlay-scrollbar>  (opt: v-overlay-scrollbar="{ minThumb: 24 }")

const STYLE_ID = 'ov-scrollbar-styles';
const HOST_CLASS = 'ov-scrollbar-host';
const THUMB_CLASS = 'ov-thumb';
const DEFAULT_MIN_THUMB = 24;
const EDGE_GAP = 2;

// Class-scoped so only bound elements lose their native scrollbar (P1 fix). The
// thumb styles ship here too so the directive is self-contained.
const STYLE_CONTENT = `
.${HOST_CLASS} { scrollbar-width: none; -ms-overflow-style: none; }
.${HOST_CLASS}::-webkit-scrollbar { display: none; }
.${THUMB_CLASS} {
  position: absolute;
  width: 6px;
  border-radius: 3px;
  opacity: 0;
  pointer-events: none;
  touch-action: none;
  z-index: 1;
  background: rgba(0, 0, 0, 0.3);
  transition: opacity 0.25s ease;
}
.${THUMB_CLASS}:hover, .${THUMB_CLASS}.ov-thumb--dragging {
  background: rgba(0, 0, 0, 0.45);
}
.dark .${THUMB_CLASS} { background: rgba(255, 255, 255, 0.3); }
.dark .${THUMB_CLASS}:hover, .dark .${THUMB_CLASS}.ov-thumb--dragging {
  background: rgba(255, 255, 255, 0.45);
}
`;

let styleRefCount = 0;

const acquireStyles = () => {
  styleRefCount += 1;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE_CONTENT;
  document.head.appendChild(style);
};

const releaseStyles = () => {
  styleRefCount = Math.max(0, styleRefCount - 1);
  if (styleRefCount > 0) return;
  const style = document.getElementById(STYLE_ID);
  if (style) style.remove();
};

const thumbHeightFor = (clientHeight, scrollHeight, minThumb) =>
  Math.max((clientHeight / scrollHeight) * clientHeight, minThumb);

export const overlayScrollbar = {
  inserted(el, binding) {
    const minThumb = binding?.value?.minThumb ?? DEFAULT_MIN_THUMB;
    acquireStyles();
    el.classList.add(HOST_CLASS);

    // The thumb is an absolute child of el, so el must be its containing block.
    // Force it only when the host stylesheet hasn't; restored on unbind.
    const priorPosition = el.style.position;
    if (window.getComputedStyle(el).position === 'static') {
      el.style.position = 'relative';
    }
    const rtl = window.getComputedStyle(el).direction === 'rtl';

    const thumb = document.createElement('div');
    thumb.className = THUMB_CLASS;
    thumb.setAttribute('aria-hidden', 'true');
    el.appendChild(thumb);

    const state = {
      thumb,
      priorPosition,
      raf: null,
      needsMeasure: true,
      contentHeight: null,
      resizeObserver: null,
      mutationObserver: null,
      dragging: false,
      dragStartY: 0,
      dragStartTop: 0,
    };
    el.__ovScrollbar = state;

    // el.scrollHeight includes the thumb itself: an absolutely positioned child
    // extends the scroll range of its container. When the content shrinks under
    // a far-scrolled thumb (switching a long conversation for a short one), the
    // stale thumb keeps the range inflated and every reposition re-derives from
    // the height it just caused — the panel stays scrolled into blank space
    // until a remount. So geometry never comes from a live scrollHeight read:
    // it is measured with the thumb out of layout (which also lets the browser
    // clamp scrollTop back into the real range) and cached; scroll cannot
    // change it, so only content changes re-measure.
    const measureContentHeight = () => {
      thumb.style.display = 'none';
      const height = el.scrollHeight;
      thumb.style.display = '';
      return height;
    };

    const contentScrollHeight = () => state.contentHeight ?? el.scrollHeight;

    const hideThumb = () => {
      thumb.style.opacity = '0';
      thumb.style.pointerEvents = 'none';
      // A hidden thumb still occupies its absolute position — park it at the
      // origin so it can't hold the scroll range open on its own.
      thumb.style.top = '0px';
    };

    const position = () => {
      const { clientHeight } = el;
      const scrollHeight = contentScrollHeight();
      if (scrollHeight <= clientHeight) {
        hideThumb();
        return;
      }
      const height = thumbHeightFor(clientHeight, scrollHeight, minThumb);
      const maxTravel = clientHeight - height;
      const scrollRange = scrollHeight - clientHeight;
      // Clamp to the measured content: right after a shrink el.scrollTop may
      // still sit beyond the real range (the browser only clamps it once the
      // thumb stops inflating the overflow), and drawing from the raw value
      // would push the thumb back out of the content.
      const scrollTop = Math.min(Math.max(el.scrollTop, 0), scrollRange);
      const offset = maxTravel * (scrollTop / scrollRange);
      thumb.style.height = `${height}px`;
      // Absolute child scrolls with the content, so add scrollTop to pin the
      // thumb to the viewport — ratio math only, no per-event getBoundingClientRect.
      thumb.style.top = `${scrollTop + offset}px`;
      thumb.style[rtl ? 'left' : 'right'] = `${EDGE_GAP}px`;
      thumb.style.opacity = '1';
      thumb.style.pointerEvents = 'auto';
    };

    // Coalesce bursts (scroll events, observer callbacks) into one layout
    // write per frame (P3).
    const schedule = () => {
      if (state.raf) return;
      state.raf = window.requestAnimationFrame(() => {
        state.raf = null;
        if (state.needsMeasure) {
          state.needsMeasure = false;
          state.contentHeight = measureContentHeight();
        }
        position();
      });
    };

    const scheduleMeasure = () => {
      state.needsMeasure = true;
      schedule();
    };

    const onPointerMove = e => {
      if (!state.dragging) return;
      const { clientHeight } = el;
      const scrollHeight = contentScrollHeight();
      if (scrollHeight <= clientHeight) return;
      const height = thumbHeightFor(clientHeight, scrollHeight, minThumb);
      const maxTravel = clientHeight - height;
      if (maxTravel <= 0) return;
      const deltaY = e.clientY - state.dragStartY;
      el.scrollTop =
        state.dragStartTop + (deltaY / maxTravel) * (scrollHeight - clientHeight);
    };

    const onPointerUp = () => {
      state.dragging = false;
      thumb.classList.remove('ov-thumb--dragging');
      thumb.removeEventListener('pointermove', onPointerMove);
      thumb.removeEventListener('pointerup', onPointerUp);
      thumb.removeEventListener('pointercancel', onPointerUp);
    };

    const onPointerDown = e => {
      state.dragging = true;
      state.dragStartY = e.clientY;
      state.dragStartTop = el.scrollTop;
      thumb.classList.add('ov-thumb--dragging');
      try {
        thumb.setPointerCapture(e.pointerId);
      } catch {
        // Synthetic pointer events (tests/automation) have no live pointer to
        // capture; the drag still works through the element listeners.
      }
      thumb.addEventListener('pointermove', onPointerMove);
      thumb.addEventListener('pointerup', onPointerUp, { once: true });
      thumb.addEventListener('pointercancel', onPointerUp, { once: true });
      e.preventDefault();
    };

    Object.assign(state, {
      onScroll: schedule,
      onMeasure: scheduleMeasure,
      onPointerDown,
      onPointerMove,
      onPointerUp,
    });

    el.addEventListener('scroll', schedule, { passive: true });
    // Media (avatars, attachments) finishing to load grows scrollHeight with
    // no childList mutation and no host resize — capture their load events.
    // Images fire `load`; video/audio fire `loadedmetadata` once sized.
    el.addEventListener('load', scheduleMeasure, { capture: true, passive: true });
    el.addEventListener('loadedmetadata', scheduleMeasure, {
      capture: true,
      passive: true,
    });
    thumb.addEventListener('pointerdown', onPointerDown);

    // The thumb must be correct without any scroll event: on mount, when async
    // content lands (conversations/contacts loading in), and when the box
    // resizes. These are exactly the moments the content height can change, so
    // they re-measure; plain scroll frames reuse the cache.
    if (typeof ResizeObserver !== 'undefined') {
      state.resizeObserver = new ResizeObserver(scheduleMeasure);
      state.resizeObserver.observe(el);
    }
    if (typeof MutationObserver !== 'undefined') {
      state.mutationObserver = new MutationObserver(scheduleMeasure);
      state.mutationObserver.observe(el, { childList: true, subtree: true });
    }
    scheduleMeasure();
  },

  unbind(el) {
    const state = el.__ovScrollbar;
    if (!state) return;
    if (state.raf) window.cancelAnimationFrame(state.raf);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    if (state.mutationObserver) state.mutationObserver.disconnect();

    el.removeEventListener('scroll', state.onScroll);
    el.removeEventListener('load', state.onMeasure, { capture: true });
    el.removeEventListener('loadedmetadata', state.onMeasure, {
      capture: true,
    });
    const { thumb } = state;
    thumb.removeEventListener('pointerdown', state.onPointerDown);
    thumb.removeEventListener('pointermove', state.onPointerMove);
    thumb.removeEventListener('pointerup', state.onPointerUp);
    thumb.removeEventListener('pointercancel', state.onPointerUp);
    thumb.remove();

    el.classList.remove(HOST_CLASS);
    el.style.position = state.priorPosition;
    delete el.__ovScrollbar;
    releaseStyles();
  },
};

export default overlayScrollbar;
